import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  DatePicker,
  Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import { fetchData, generateReport } from "../../../features/fetch/fetchSlice";
import dayjs from "dayjs";

const { TextArea } = Input;
const EvaluationAdd = () => {
  const urlProjects = "main_project/fetch_mainprojects";
  const urlPathway = "api/v2/irrigationPathway/get";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { activeObjEvaluation, dataProjects, dataPathway } = useSelector(
    (state) => ({
      activeObjEvaluation: state.obj.activeObj["evaluation"],
      dataProjects: state.fetch.data[urlProjects] || [],
      dataPathway: state.fetch.data[urlPathway] || [],
    })
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  async function handleCancel() {
    await navigate("/rob/project-evaluations");
    await setDownload(false);
  }

  const [download, setDownload] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const handleDownload = async (item) => {
    const res = await dispatch(
      generateReport({
        url: "api/v1/reports/directReport",
        reportName: "PROJECT_EVALUATION_REPORT",
        itemId: item,
        reportFormat: "PDF",
      })
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;

      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];

      let fileName = "report";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      await toast.success(`${fileName} downloaded successfully`);
    } else {
      toast.error("Failed to download file");
    }
  };

  const onFinish = async (data) => {
    data.evaId = activeObjEvaluation?.evaId ? activeObjEvaluation?.evaId : null;

    data.evaPathwayId = Array.isArray(data?.evaPathwayId)
      ? data?.evaPathwayId.join(",")
      : data?.evaPathwayId;

    const res = await dispatch(
      save({
        ...data,
        url: "evaluation/save_evaluation",
      })
    );
    if (res?.payload?.success) {
      if (download) {
        await handleDownload(res?.payload?.data?.evaId);
      }
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const initialFormValues = {
    ...activeObjEvaluation,
    evaStartDate: activeObjEvaluation?.projStartDate
      ? dayjs(new Date(activeObjEvaluation?.projStartDate))
      : null,
    evaEndDate: activeObjEvaluation?.projExpectedEndDate
      ? dayjs(new Date(activeObjEvaluation?.projExpectedEndDate))
      : null,
    evaEvalDate: activeObjEvaluation?.evaEvalDate
      ? dayjs(new Date(activeObjEvaluation?.evaEvalDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjEvaluation,
      evaStartDate: activeObjEvaluation?.evaStartDate
        ? dayjs(new Date(activeObjEvaluation?.evaStartDate))
        : null,
      evaEndDate: activeObjEvaluation?.evaEndDate
        ? dayjs(new Date(activeObjEvaluation?.evaEndDate))
        : null,
      evaEvalDate: activeObjEvaluation?.evaEvalDate
        ? dayjs(new Date(activeObjEvaluation?.evaEvalDate))
        : null,
    });
    if (activeObjEvaluation?.evaPathwayId) {
      form.setFieldsValue({
        evaPathwayId: activeObjEvaluation?.evaPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjEvaluation, form]);

  const handleProjectNumberChange = (value) => {
    const selectedProject = dataProjects?.find(
      (item) => item?.projectName === value
    );

    if (selectedProject) {
      form?.setFieldsValue({
        evaProjectId: selectedProject?.projectId,
        evaNumber: selectedProject?.projectNumber || "",
        evaStatus: selectedProject?.projectStatus || "",
        evaThreshold: selectedProject?.projectThresholdId || "",
        evaStartDate: selectedProject?.projectStartDate
          ? dayjs(new Date(selectedProject?.projectStartDate))
          : null,
        evaEndDate: selectedProject?.projectEndDate
          ? dayjs(new Date(selectedProject?.projectEndDate))
          : null,
        evaTotalCost: selectedProject?.projectCost || "",
      });
    } else {
      form?.setFieldsValue({
        evaNumber: "",
        evaStatus: "",
        evaStartDate: "",
        evaEndDate: "",
        evaTotalCost: "",
      });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
  }, []);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Project Evaluations", path: "/rob/project-evaluations" },
          { title: "Evaluation Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">New Project Evaluation</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title">Project Basic Data</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="evaName"
                className=""
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please select project name",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={dataProjects?.map((item, index) => {
                    return {
                      value: item?.projectName,
                      label: item?.projectName,
                    };
                  })}
                  onChange={handleProjectNumberChange}
                  onDropdownVisibleChange={() => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        start: 0,
                        limit: null,
                      })
                    );
                  }}
                  showSearch
                  onSearch={(value) => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        start: 0,
                        limit: null,
                        projectName: value || null,
                      })
                    );
                  }}
                  optionFilterProp="label"
                  filterOption={false}
                />
              </Form.Item>
              <Form.Item
                name="evaProjectId"
                className="hidden"
                label={"Evaluation Id"}
                rules={[
                  {
                    required: true,
                    message: "Please select evaluation id",
                  },
                ]}
              >
                <Input disabled className="input " />
              </Form.Item>
              <Form.Item
                name="evaNumber"
                className=""
                label={"Project Number"}
                rules={[
                  {
                    required: true,
                    message: "Please select project number",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="evaTotalCost"
                label={"Total Project Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add total project cost",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="evaContract"
                label={"Contract(s)"}
                rules={[
                  {
                    required: false,
                    message: "Please add Contract",
                  },
                ]}
              >
                <InputNumber
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="evaThreshold"
                className=""
                label={"Project Threshold"}
                rules={[
                  {
                    required: false,
                    message: "Please select project threshold",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="evaSector"
                className=""
                label={"Sector"}
                rules={[
                  {
                    required: true,
                    message: "Please select sector",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                label="Start Date"
                name="evaStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Expected End Date"
                name="evaEndDate"
                rules={[
                  {
                    required: true,
                    message: "Expected end date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">
              Executive Summary Introduction
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="evaBackgroundInfo"
                label={"Backround"}
                rules={[
                  {
                    required: true,
                    message: "Please add backround",
                  },
                ]}
              >
                <TextArea
                  placeholder="Backround..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaEvaluationScope"
                label={"Scope of Evaluation"}
                rules={[
                  {
                    required: true,
                    message: "Please add scope of evaluation",
                  },
                ]}
              >
                <TextArea
                  placeholder="Scope of Evaluation..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>{" "}
              <Form.Item
                name="evaApproach"
                label={"Evaluation approach and methodology"}
                rules={[
                  {
                    required: true,
                    message: "Please add evaluation approach and methodology",
                  },
                ]}
              >
                <TextArea
                  placeholder="Evaluation..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaLimitation"
                label={"Limitations of Evaluation"}
                rules={[
                  {
                    required: true,
                    message: "Please add limitations of evaluation",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">Summary of Findings</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="evaPerformanceAssessment"
                label={"Project Perfomance Assement"}
                rules={[
                  {
                    required: true,
                    message: "Please add project perfomance assement",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaDesignRelevance"
                label={"Relevance of Project Design"}
                rules={[
                  {
                    required: true,
                    message: "Please add relevance of project design",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaEffectiveness"
                label={"Effectiveness in the achievement of output"}
                rules={[
                  {
                    required: true,
                    message:
                      "Please add effectiveness in the achievement of output",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaEfficiency"
                label={"Efficiency in terms of timeliness"}
                rules={[
                  {
                    required: true,
                    message: "Please add efficiency in terms of timeliness",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaImpactProgress"
                label={"Impact and Progress toward achievement"}
                rules={[
                  {
                    required: true,
                    message:
                      "Please add impact and progress toward achievement",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaSustainability"
                label={"Sustainability"}
                rules={[
                  {
                    required: true,
                    message: "Please add Sustainability",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaOtherFindings"
                label={"Any other finding"}
                rules={[
                  {
                    required: true,
                    message: "Please add any other finding",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">
              Summarize lessons learnt and recommendations
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="evaSumDesignRelevance"
                label={"Relevance of Project Design"}
                rules={[
                  {
                    required: true,
                    message: "Please add relevance of project design",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaSumEffectiveness"
                label={
                  "Effectiveness in the achievement of outcomes and outputs"
                }
                rules={[
                  {
                    required: true,
                    message:
                      "Please add effectiveness in the achievement of outcomes and outputs",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaSumEfficiency"
                label={"Efficiency in terms of timelines and resource use"}
                rules={[
                  {
                    required: true,
                    message:
                      "Please add efficiency in terms of timelines and resource use",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaImpactAndProgress"
                label={
                  "Impact and progress towards achievement of development objectives"
                }
                rules={[
                  {
                    required: true,
                    message:
                      "Please add impact and progress towards achievement of development objectives",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>

              <Form.Item
                name="evaSumSustainability"
                label={"Sustainability"}
                rules={[
                  {
                    required: true,
                    message: "Please add Sustainability",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="evaSumOtherFindings"
                label={"Any other finding"}
                rules={[
                  {
                    required: true,
                    message: "Please add any other finding",
                  },
                ]}
              >
                <TextArea
                  placeholder="info..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                label={"Pathways"}
                name="evaPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Inspection Date"
                name="evaEvalDate"
                rules={[
                  {
                    required: true,
                    message: "Inspection date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
            </div>
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>
                <div className="w-[200px]">
                  <button
                    disabled={saving || reportLoading}
                    type="submit"
                    onClick={() => setDownload(true)}
                    className="cstm-btn"
                  >
                    {saving || reportLoading ? (
                      <Spin className="blu_spin" />
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>
                <div className="w-[150px]">
                  <button
                    disabled={saving || reportLoading}
                    key="submit"
                    type="submit"
                    className="cstm-btn-2"
                    onClick={() => setDownload(false)}
                  >
                    {saving ? (
                      <Spin />
                    ) : activeObjEvaluation?.evaId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default EvaluationAdd;
